import { type Dot, OVERSCAN, gutter, ramp, seamInset, sine } from './dotField';

/**
 * Draws the field, once, for a given scroll position.
 *
 * Called from the same animation frame that moves everything else in the
 * atmosphere, and only from there: on a frame where the page did not move,
 * nothing here runs at all.
 */

export interface Frame {
  /** Viewport, in CSS pixels. */
  width: number;
  height: number;
  /** Where the reader is, and how far through the page. */
  scroll: number;
  progress: number;
  /** Milliseconds since the page began, for the part that moves on its own. */
  time: number;
  /** The current seam, in screen coordinates, or null when there is none. */
  seam: { y: number; half: number; strength: number; drawn: number } | null;
  /** Boxes the field must leave alone, in screen coordinates. */
  keepOut: { x0: number; y0: number; x1: number; y1: number }[];
  /** Still, for a reader who asked for less motion. */
  still: boolean;
}

/** How far outside a keep-out box the field returns to full strength. */
const KEEP_OUT_FADE = 16;

/** A region of the canvas that has ink in it, or had ink in it last frame. */
export interface Patch {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** How far a dot travels between crest and trough. */
const AMPLITUDE = 11;

/** How fast the wave crosses the page as you scroll. */
const TRAVEL = 0.0022;

/* ── Standing still ─────────────────────────────────────────────────────────
   Everything above answers the scroll, and the scroll alone: stop moving and
   the field froze, which is not what a field of particles does. So two things
   run on a clock instead.

   The first is the wave itself, which now travels a little without being
   pushed — slowly enough that you would not catch it starting, fast enough that
   the surface is never the same twice.

   The second is each dot's own drift: a small circle, at its own rate and from
   its own place in it, so what you see is a few hundred things floating rather
   than one sheet breathing. Two pixels is the whole of it. Any more and it
   stops being texture and becomes something happening. */
const FLOAT = 2.2;

/** How fast a dot goes round its own small circle, in radians a second. */
const FLOAT_RATE = 0.52;

/** How fast the wave crosses the page unaided, in radians a second. */
const WAVE_RATE = 0.16;

/** Rate the whole field drifts against the page, which is what puts it behind. */
const DRIFT = 0.34;

/** Opacity of a dot at the crest of the wave, before any masking. */
const PEAK = 1;

/** …and in the trough. The gap between the two is most of the depth, but not
 *  so much of it that half the field disappears and the rest reads as specks. */
const TROUGH = 0.44;

/**
 * The sprite every dot is drawn from.
 *
 * One soft-edged disc, rendered once at high resolution and scaled down per
 * dot. Drawing each dot as a path instead would mean a few hundred fills a
 * frame and a hard edge on every one of them; a scaled sprite is one composite
 * each, and the soft edge is what stops the field looking punched out.
 */
export function makeSprite(colour: string): HTMLCanvasElement {
  const size = 32;
  const sprite = document.createElement('canvas');
  sprite.width = size;
  sprite.height = size;
  const ctx = sprite.getContext('2d');
  if (!ctx) return sprite;
  const half = size / 2;
  const gradient = ctx.createRadialGradient(half, half, 0, half, half, half);
  gradient.addColorStop(0, colour);
  gradient.addColorStop(0.62, colour);
  gradient.addColorStop(1, 'transparent');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return sprite;
}

/**
 * Draws the field and returns the parts of the canvas it used.
 *
 * `painted` is what the last call returned. Only those regions and the ones
 * about to be drawn are cleared, which is the difference between this costing
 * something and this costing nothing: on a phone the field is one band a
 * hundred-odd pixels deep, and clearing the whole screen for it every frame
 * was most of the work being done. Pass an empty list to clear nothing, or the
 * whole canvas when in doubt.
 */
export function paintDots(
  ctx: CanvasRenderingContext2D,
  sprite: HTMLCanvasElement,
  dots: Dot[],
  frame: Frame,
  painted: Patch[]
): Patch[] {
  const { width, height, scroll, time, seam, keepOut, still } = frame;

  const margin = gutter(width);
  const inset = seamInset(width);
  const tile = height + OVERSCAN * 2;
  const drift = still ? 0 : scroll * DRIFT;
  const seconds = still ? 0 : time / 1000;
  const travel = still ? 0 : scroll * TRAVEL + seconds * WAVE_RATE;

  /* Where ink can land this frame: down both margins, and across whatever seam
     is showing. Everything outside is untouched, and stays untouched. */
  const reach = AMPLITUDE + FLOAT + 8;
  const patches: Patch[] = [];
  if (margin > 0) {
    const wide = margin + reach;
    patches.push({ x: 0, y: 0, w: wide, h: height });
    patches.push({ x: width - wide, y: 0, w: wide, h: height });
  }
  let bandTop = Infinity;
  let bandBottom = -Infinity;
  if (seam && seam.strength > 0.01) {
    bandTop = seam.y - seam.half - reach;
    bandBottom = seam.y + seam.half + reach;
    patches.push({ x: 0, y: bandTop, w: width, h: bandBottom - bandTop });
  }

  for (let i = 0; i < painted.length; i += 1) {
    const p = painted[i];
    ctx.clearRect(p.x, p.y, p.w, p.h);
  }
  for (let i = 0; i < patches.length; i += 1) {
    const p = patches[i];
    ctx.clearRect(p.x, p.y, p.w, p.h);
  }
  if (!patches.length) return patches;

  /* With no margin — every phone — the only ink is the seam band, so a dot
     nowhere near it is not worth any of the arithmetic below. */
  const bandOnly = margin <= 0;

  ctx.globalAlpha = 1;

  for (let i = 0; i < dots.length; i += 1) {
    const dot = dots[i];

    /* Position. The wave is read from where the dot is in the document, not
       where it is on the screen, so it travels with the page rather than
       standing still while the page moves through it. */
    const world = dot.y - drift;
    let y = ((world % tile) + tile) % tile;
    y -= OVERSCAN;
    if (y < -40 || y > height + 40) continue;
    if (bandOnly && (y < bandTop - reach || y > bandBottom + reach)) continue;

    let x = dot.x;
    let lift = 0;

    if (!still) {
      /* Two waves: a long one that gives the field its shape and a short one
         across it, so the surface has a grain rather than a single roll. */
      const long = sine(dot.x * 0.0052 + world * 0.0034 + travel + dot.phase);
      const short = sine(dot.x * 0.0155 - world * 0.011 - travel * 2.1 + dot.phase);
      lift = long * 0.72 + short * 0.28;
      y += lift * AMPLITUDE;
      /* A little sideways as well as up: a surface rolling, not a row bobbing. */
      x += sine(world * 0.0061 + travel * 1.4 + dot.phase) * AMPLITUDE * 0.35;

      /* And the float. A circle rather than a bob, because a bob is a row of
         things going up and down together however you stagger it, and a circle
         is not: two dots a moment apart in phase are moving in different
         directions, which is what makes a still page look alive rather than
         look like it is ticking. */
      const spin = seconds * FLOAT_RATE + dot.phase * 2.3;
      x += sine(spin) * FLOAT;
      y += sine(spin + Math.PI / 2) * FLOAT;
    }

    /* Depth. Height in the wave sets size and brightness together, which is
       what reads as near and far rather than as up and down. */
    const depth = (lift + 1) / 2;

    /* ── Where it may be seen ─────────────────────────────────────────────
       The margins, always; and the seam band, while there is one. Whichever
       allows more, wins — so a dot in the margin beside a seam is simply at
       full strength rather than fighting between two rules. */
    const fromEdge = Math.min(x, width - x);
    let visible = margin > 0 ? 1 - ramp(margin * 0.55, margin, fromEdge) : 0;

    if (seam) {
      const near = 1 - ramp(0.55, 1, Math.abs(y - seam.y) / seam.half);
      /* Held off the left and right edges, where the section rail lives. */
      const clear = ramp(inset * 0.3, inset, fromEdge);
      /* The draw-in, wiping from the left as the seam rises into view. Soft,
         so what arrives is a tide rather than a shutter. */
      const front = 1 - ramp(seam.drawn - 0.12, seam.drawn + 0.04, x / width);
      const strength = near * clear * front * seam.strength;
      if (strength > visible) visible = strength;
    }

    /* The page's margin is also where the section rail lives, and a rail read
       through a field of dots is a rail nobody can read. Anything that says so
       pushes the field off itself, with a soft edge so what happens around it
       is a clearing rather than a hole. */
    for (let k = 0; k < keepOut.length && visible > 0; k += 1) {
      const box = keepOut[k];
      const away = Math.max(box.x0 - x, 0, x - box.x1, box.y0 - y, 0, y - box.y1);
      if (away < KEEP_OUT_FADE) visible *= ramp(0, KEEP_OUT_FADE, away);
    }

    if (visible <= 0.012) continue;

    const alpha = visible * (TROUGH + (PEAK - TROUGH) * depth);
    if (alpha <= 0.012) continue;

    const radius = dot.r * (0.72 + 0.56 * depth);
    const span = radius * 2.9;
    ctx.globalAlpha = alpha;
    ctx.drawImage(sprite, x - span / 2, y - span / 2, span, span);
  }

  ctx.globalAlpha = 1;
  return patches;
}
